/**
 * The three identity operations of `authors-api`, typed off the generated OpenAPI types and
 * nothing else.
 *
 * NOTHING HERE HAND-WRITES A REQUEST OR A RESPONSE SHAPE. Every body below is pulled out of
 * `operations[…]` in shared/types/generated/authors-api.d.ts, which is generated from the
 * committed spec. That is not decoration: it means a spec change that alters one of these shapes
 * turns this file red, and it means an operation that does not exist cannot be called by mistake —
 * there is no type to write it against.
 *
 * WHY THE IMPORT IS A LONG RELATIVE PATH. adr/0013 rules "no path aliases, plain relative
 * imports", and 12-build-plan.md fails M0 on a `paths` entry in any tsconfig. The generated
 * file is a `.d.ts`, so this import is erased entirely at build time and costs the bundle nothing.
 *
 * THE FOUR OPERATIONS, AND THE ONE THAT CARRIES NO CREDENTIAL:
 *   POST /v1/handoff/codes     createCabinetHandoffCode      bearer  — leg one, from the phone or
 *                                                                     from the website
 *   POST /v1/handoff/exchange  exchangeCabinetHandoffCode    NONE    — `security: []` on purpose:
 *                                                                     "the code IS the credential"
 *   GET  /v1/me                getAuthorSession              bearer  — the one read the whole
 *                                                                     cabinet renders itself against
 *   POST /v1/authorship        becomeAuthor                  bearer  — "Become an author"
 *                                                                     (design 25): the row, made
 *
 * Design 25 (section 3.4) added `AuthorSession.canWriteDrafts` and `becomeAuthor`; both are read
 * off the generated types like everything else here.
 */
import type { operations } from '../../../../../../shared/types/generated/authors-api';
import { classifyFailure } from './failure';
import type { ApiFailure } from './failure';
import { NetworkUnreachable } from './http';
import type { Transport } from './http';
import { authorsApiTransport } from './transport';

/** The success bodies, taken straight off the generated operation types. */
export type HandoffCode =
  operations['createCabinetHandoffCode']['responses'][201]['content']['application/json'];
export type HandoffExchangeResult =
  operations['exchangeCabinetHandoffCode']['responses'][200]['content']['application/json'];
export type AuthorSession =
  operations['getAuthorSession']['responses'][200]['content']['application/json'];
/** `becomeAuthor`'s answer: the session, exactly as `getAuthorSession` answers it now. */
export type BecomeAuthorResult =
  operations['becomeAuthor']['responses'][200]['content']['application/json'];

/** The request bodies, likewise. `ClientKind` is the seeded vocabulary: ios, android, web. */
export type HandoffCodeRequest =
  operations['createCabinetHandoffCode']['requestBody']['content']['application/json'];
export type ClientKind = HandoffCodeRequest['client'];

/**
 * Every call returns one of these. A refusal is a VALUE, not an exception.
 *
 * That choice is the 503 rule again: a thrown 503 gets caught by whatever catch block is nearest,
 * and the nearest catch block in a sign-in screen is usually the one that signs people out.
 */
export type ApiResult<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly failure: ApiFailure };

async function call<T>(
  transport: Transport,
  request: Parameters<Transport>[0],
  signal: AbortSignal | undefined,
): Promise<ApiResult<T>> {
  let response;
  try {
    response = await transport(request, signal);
  } catch (error) {
    if (error instanceof NetworkUnreachable) {
      return { ok: false, failure: { kind: 'unreachable' } };
    }
    throw error;
  }

  if (response.status >= 200 && response.status < 300) {
    return { ok: true, value: response.body as T };
  }

  return { ok: false, failure: classifyFailure(response) };
}

export interface CallOptions {
  /** Injected by tests; production uses the one seam in ./transport.ts. */
  readonly transport?: Transport;
  readonly signal?: AbortSignal;
}

/**
 * LEG ONE. Mint a single-use hand-off code for the signed-in caller.
 *
 * THE BODY HAS EXACTLY ONE PROPERTY AND THERE IS NOWHERE TO PUT A UID. "The subject is taken only
 * from the verified token ... a body carrying a uid is a schema violation answered 400 — the
 * absence is the control, not an ignore rule" (docs/design/07-api/authors-api.yaml). This
 * signature has no uid parameter for the same reason: the caller could not supply one if it tried.
 *
 * `client: 'web'` IS ALREADY IN THE VOCABULARY. `ClientKind` is `ios | android | web`, so the
 * website-to-cabinet leg needs no new operation — see ../handoff/website-handoff.ts.
 */
export function createCabinetHandoffCode(
  client: ClientKind,
  bearerToken: string,
  options: CallOptions = {},
): Promise<ApiResult<HandoffCode>> {
  const body: HandoffCodeRequest = { client };
  return call<HandoffCode>(
    options.transport ?? authorsApiTransport,
    { method: 'POST', path: '/v1/handoff/codes', body, bearerToken },
    options.signal,
  );
}

/**
 * LEG TWO. Exchange the code for a Firebase custom token.
 *
 * NO CREDENTIAL IS SENT, AND THAT IS DELIBERATE, NOT AN OVERSIGHT. The operation carries
 * `security: []`: "the code IS the credential, and a caller who had a Firebase token would not
 * need the hand-off" (authors-api.yaml). Sending a bearer token here would be a change to the
 * contract, so this function has no parameter for one.
 *
 * ITS 401 MEANS SOMETHING DIFFERENT FROM EVERY OTHER 401 ON THIS SURFACE. It is
 * `HANDOFF_CODE_INVALID` — unknown, expired or already used, one answer for all three — and it says
 * nothing about the caller's session. The screen must not sign anybody out on it.
 */
export function exchangeCabinetHandoffCode(
  code: string,
  options: CallOptions = {},
): Promise<ApiResult<HandoffExchangeResult>> {
  return call<HandoffExchangeResult>(
    options.transport ?? authorsApiTransport,
    { method: 'POST', path: '/v1/handoff/exchange', body: { code } },
    options.signal,
  );
}

/**
 * THE ONE READ THE CABINET RENDERS ITSELF AGAINST. Four gates in one payload since design 25
 * (section 3.4): `canApply` (no author row yet, or an applicant), `canUseEditor` (approved authors
 * only), `canWriteDrafts` (applicants and approved authors) and `cabinetReachable` (ANY author row:
 * an applicant, an approved author and a disabled one). Plus `termsAcceptanceRequired` (approved
 * and owing a submission document), `disabledReason`, `slug`, `slugFrozen` and `applicationState`.
 *
 * The capability and the disabled flag come from OUR table and never from a Firebase custom claim,
 * because a claim lags up to an hour behind a disable (adr/0010-firebase-integration.md). So this
 * read reflects a disable immediately, and it is the only thing that does.
 *
 * Declared answers are 200, 401 and 503 — and only 503 is the one that must never end a session.
 */
export function getAuthorSession(
  bearerToken: string,
  options: CallOptions = {},
): Promise<ApiResult<AuthorSession>> {
  return call<AuthorSession>(
    options.transport ?? authorsApiTransport,
    { method: 'GET', path: '/v1/me', bearerToken },
    options.signal,
  );
}

/**
 * "BECOME AN AUTHOR" (design 25, P16 and section 3.4): `POST /v1/authorship`, `becomeAuthor`. It
 * makes the author row - an applicant's - and answers the session, so the caller is an applicant
 * the moment it lands. Signed in with a real provider (`@RequiresRealSignIn`, G30) and IDEMPOTENT:
 * a person who already has a row is answered with their own session. No body: the subject is the
 * token, as on every operation here.
 *
 * It is the ONLY way the cabinet makes the row on purpose: "a reader who merely signs in is not
 * made an applicant by accident" (design 24, T22 D6). The five application writes that also make
 * it stay as they are, because the iOS app uses two of them. Declared answers: 200 (the session),
 * 401, 422 (G30: an account whose only sign-in is anonymous; the gate sends those to the link
 * prompt first) and 503.
 */
export function becomeAuthor(
  bearerToken: string,
  options: CallOptions = {},
): Promise<ApiResult<BecomeAuthorResult>> {
  return call<BecomeAuthorResult>(
    options.transport ?? authorsApiTransport,
    { method: 'POST', path: '/v1/authorship', bearerToken },
    options.signal,
  );
}

/** `requestVideoAccess`'s answer: the session, whose `videoAccess` now reads `requested`. */
export type RequestVideoAccessResult =
  operations['requestVideoAccess']['responses'][200]['content']['application/json'];

/**
 * "Request access to video" (v1.0.29): `POST /v1/video-access`. Recorded once and raised to staff;
 * asking again, or with access already granted, changes nothing. No body. Answers the session.
 */
export function requestVideoAccess(
  bearerToken: string,
  options: CallOptions = {},
): Promise<ApiResult<RequestVideoAccessResult>> {
  return call<RequestVideoAccessResult>(
    options.transport ?? authorsApiTransport,
    { method: 'POST', path: '/v1/video-access', bearerToken },
    options.signal,
  );
}

/** `requestStreamAccess`'s answer: the session, whose `streamAccess` now reads `requested`. */
export type RequestStreamAccessResult =
  operations['requestStreamAccess']['responses'][200]['content']['application/json'];

/**
 * "Request access to live streams" (1.2.0): `POST /v1/stream-access`, asked and answered as
 * `requestVideoAccess` is, by a switch of its own.
 */
export function requestStreamAccess(
  bearerToken: string,
  options: CallOptions = {},
): Promise<ApiResult<RequestStreamAccessResult>> {
  return call<RequestStreamAccessResult>(
    options.transport ?? authorsApiTransport,
    { method: 'POST', path: '/v1/stream-access', bearerToken },
    options.signal,
  );
}
